function escapePropertyName(propertyName: string) {
    return propertyName.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

function isSimplePropertyName(propertyName: string) {
    return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(propertyName);
}

// A "function" segment represents a trailing Postgres SQL/JSON-path method like `size()`.
// It's structurally distinct from a property name so the picker can render a Function dropdown
// instead of an object property, and so the serializer emits `.size()` (not `['size()']`).
export interface JsonPathFunctionSegment {
    kind: "function";
    name: string;
}

export type JsonPathSegment = string | number | JsonPathFunctionSegment;

function isFunctionSegmentToken(segment: string): boolean {
    return /^[A-Za-z_][A-Za-z0-9_]*\(\)$/.test(segment);
}

export function jsonPathToSegments(jsonPath: string): JsonPathSegment[] {
    if (!jsonPath || jsonPath === "$") {
        return [];
    }

    const pathSegments: JsonPathSegment[] = [];
    let index = jsonPath.startsWith("$") ? 1 : 0;

    while (index < jsonPath.length) {
        const currentCharacter = jsonPath[index];

        if (currentCharacter === ".") {
            index += 1;
            const nextSeparator = jsonPath.slice(index).search(/[.[]/);
            if (nextSeparator === -1) {
                const segment = jsonPath.slice(index);
                if (segment) {
                    pathSegments.push(
                        isFunctionSegmentToken(segment)
                            ? { kind: "function", name: segment }
                            : segment,
                    );
                }
                break;
            }

            const segment = jsonPath.slice(index, index + nextSeparator);
            if (segment) {
                pathSegments.push(
                    isFunctionSegmentToken(segment)
                        ? { kind: "function", name: segment }
                        : segment,
                );
            }
            index += nextSeparator;
            continue;
        }

        if (currentCharacter === "[") {
            const closingBracket = jsonPath.indexOf("]", index);
            if (closingBracket === -1) {
                break;
            }

            const bracketValue = jsonPath.slice(index + 1, closingBracket);
            if (
                (bracketValue.startsWith("'") && bracketValue.endsWith("'")) ||
                (bracketValue.startsWith('"') && bracketValue.endsWith('"'))
            ) {
                const propertyName = bracketValue
                    .slice(1, -1)
                    .replace(/\\'/g, "'")
                    .replace(/\\"/g, '"')
                    .replace(/\\\\/g, "\\");
                pathSegments.push(propertyName);
            } else if (/^\d+$/.test(bracketValue)) {
                pathSegments.push(Number(bracketValue));
            }

            index = closingBracket + 1;
            continue;
        }

        index += 1;
    }

    return pathSegments;
}

export function segmentsToJsonPath(pathSegments: JsonPathSegment[]): string {
    if (!pathSegments.length) {
        return "$";
    }

    return pathSegments.reduce<string>((jsonPath, segment) => {
        if (typeof segment === "number") {
            return `${jsonPath}[${segment}]`;
        }

        if (typeof segment !== "string") {
            return `${jsonPath}.${segment.name}`;
        }

        if (isSimplePropertyName(segment)) {
            return `${jsonPath}.${segment}`;
        }

        return `${jsonPath}['${escapePropertyName(segment)}']`;
    }, "$");
}
